<template>
  <q-expansion-item
    :model-value="expand"
    group="sessions"
    expand-separator
    :hide-expand-icon="hideExpandIcon"
    :header-class="{
      'text-primary': true,
      'text-bold': bold
    }"
    :class="{
      'unfocused-activity': unfocused,
    }"
    @update:model-value="onSwitchSessionActivity"
  >
    <template #header="props">
      <q-item-section
        avatar
        style="cursor: pointer!important;"
      >
        <q-avatar :icon="getSessionActivityTypeIcon(sessionActivity)">
          <q-badge
            floating
            :color="getSessionActivityStatusColor(sessionActivity)"
            :data-e2e="`badge-session-activity-status-${sessionActivity.status}`"
          >
            <q-spinner-dots
              v-if="sessionActivity.status === 'in-progress'"
            />
            <q-icon
              v-else
              :name="getSessionActivityStatusIcon(sessionActivity)"
            />
          </q-badge>
        </q-avatar>
        <q-tooltip>
          {{ getSessionActivityTypeAndStatusLabel(sessionActivity) }}
        </q-tooltip>
      </q-item-section>
      <q-item-section
        class="cursor-pointer activity-item"
      >
        <div class="full-width overflow-hidden-y">
          <q-item-label lines="3">
            {{ sessionActivityTitle }}
          </q-item-label>
          <q-item-label
            caption
          >
            {{ formattedSessionActivityDistance }}
            {{ formattedSessionActivityDate }}
            <template
              v-if="sessionActivity.status === 'in-progress'"
            >
              <q-spinner-dots
                class="q-ml-sm"
              />
              {{ $t('sessions.sessionCard.sessionActivityInProgress') }}
            </template>
            <appointment-chip
              v-if="hasLinkedAppointment && !!sessionActivity.appointment"
              :appointment="linkedAppointment"
              :is-group-session="isGroupSession"
              :disable="!canEditLinkedAppointment || disableChangeLinkedAppointment || notesSending"
              clickable
              @click="onLinkAppointment"
            />
          </q-item-label>
        </div>
        <q-icon
          v-if="isBackupDifferent"
          name="warning"
          color="red"
          size="sm"
          class="q-ml-md q-mt-sm"
        >
          <q-tooltip>
            {{ $t('sessions.modals.sessionActivity.activityHasLocalRecoveryCopy') }}
          </q-tooltip>
        </q-icon>
      </q-item-section>
      <q-item-section
        v-if="props.expanded"
        side
      >
        <div class="row q-col-gutter-sm items-center">
          <div
            v-if="isExportableSessionActivity"
          >
            <q-btn
              v-if="hasTrainerCredentials"
              flat
              round
              dense
              color="primary-variant"
              icon="ios_share"
              @click.stop
            >
              <q-menu>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  data-e2e="button-copy-notes-clipboard"
                  @click="onCopySessionNotesToClipboard(sessionActivity)"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="text_snippet"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section
                    class="text-no-wrap"
                  >
                    {{ $t('sessions.sessionCard.textExportSessionNotesToClipboard') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="hasSession && !isSessionTypeWithoutPrograms"
                  v-ripple
                  v-close-popup
                  clickable
                  data-e2e="button-export-notes-and-data-pdf"
                  @click="onExportSessionNotesAndDataToPdf"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="fas fa-file-pdf"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section
                    class="text-no-wrap"
                  >
                    {{ $t('sessions.sessionCard.textExportSessionNotesAndDataToPdf') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  data-e2e="button-export-notes-pdf"
                  @click="onExportSessionNotesToPdf(sessionActivity)"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="fas fa-file-pdf"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section
                    class="text-no-wrap"
                  >
                    {{ $t('sessions.sessionCard.textExportSessionNotesToPdf') }}
                  </q-item-section>
                </q-item>
              </q-menu>
            </q-btn>
          </div>
          <div>
            <q-btn
              v-if="
                canUnrenderSessionActivity
                  || canReopenSessionActivity
                  || canAddLinkedAppointment
                  || canUnlinkAppointment
                  || canFinishSessionActivity
                  || showFinishAppointment
                  || hasGeolocation
                  || userCanViewAuditTrail
                  || canEditSessionActivity
                  || canDeleteSessionActivity
                  || canGoToIntegrationAppointment
              "
              flat
              round
              dense
              color="dark-variant"
              icon="more_vert"
              @click.stop
            >
              <q-menu>
                <q-list
                  style="min-width: 100px"
                >
                  <div v-if="canUnrenderSessionActivity">
                    <q-item
                      v-close-popup
                      :disable="notesSending || integrationSettings.allowScheduling"
                      clickable
                      @click="onUnrenderSessionActivity"
                    >
                      <q-item-section avatar>
                        <q-icon
                          name="lock_open"
                        />
                      </q-item-section>
                      <q-item-section class="text-no-wrap">
                        {{ $t('sessions.sessionCard.buttonUnrenderSessionActivity') }}
                      </q-item-section>
                    </q-item>
                    <q-tooltip v-if="integrationSettings.allowScheduling">
                      {{ $t('sessions.sessionCard.tooltipUnrenderNotAvailableForScheduling') }}
                    </q-tooltip>
                  </div>
                  <q-item
                    v-if="canGoToIntegrationAppointment"
                    v-close-popup
                    clickable
                    @click="onGoToIntegrationAppointment"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="link"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ goToIntegrationAppointmentLabel }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canReopenSessionActivity"
                    v-close-popup
                    :disable="notesSending"
                    clickable
                    @click="onReopenSessionActivity"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="lock_open"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.buttonReopenSessionActivity') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canAddLinkedAppointment"
                    v-close-popup
                    :disable="disableChangeLinkedAppointment || notesSending"
                    clickable
                    class="text-no-wrap"
                    @click="onLinkAppointment"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="edit_calendar"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textAddAppointment') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canUnlinkAppointment"
                    v-close-popup
                    :disable="notesSending"
                    clickable
                    class="text-no-wrap"
                    @click="onUnlinkAppointment"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="edit_calendar"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textUnlinkAppointment') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canFinishSessionActivity"
                    v-close-popup
                    clickable
                    @click="onFinishSessionActivity"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="dangerous"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.textFinishSessionActivity') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="showFinishAppointment"
                    v-close-popup
                    clickable
                    @click="finishAppointment(true)"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="dangerous"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.textFinishAppointment') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="hasGeolocation"
                    v-close-popup
                    clickable
                    :disable="offlineMode"
                    @click="onShowGeolocation"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="place"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.modals.geolocation.viewLocation') }}
                    </q-item-section>
                  </q-item>
                  <universal-tooltip
                    :show-tooltip="disableAuditTrail"
                    :text="$t('premiumUpgrade.upgradeHint')"
                  >
                    <q-item
                      v-if="userCanViewAuditTrail"
                      v-close-popup
                      :disable="disableAuditTrail"
                      clickable
                      @click="onViewAuditTrail"
                    >
                      <q-item-section
                        avatar
                        class="text-no-wrap"
                      >
                        <q-icon
                          name="fact_check"
                        />
                      </q-item-section>
                      <q-item-section class="text-no-wrap">
                        {{ $t('auditLogReports.viewAuditTrail') }}
                      </q-item-section>
                    </q-item>
                  </universal-tooltip>
                  <q-item
                    v-if="canEditSessionActivity"
                    v-close-popup
                    clickable
                    @click="onEditSessionActivity"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="edit"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.textEditSessionActivity') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canDeleteSessionActivity"
                    v-close-popup
                    clickable
                    :disable="shouldUnlinkAppointmentFromActivity || notesSending"
                    @click="onDeleteSessionActivity"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="delete"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.textDeleteSessionActivity') }}
                    </q-item-section>
                    <q-tooltip v-if="shouldUnlinkAppointmentFromActivity">
                      {{ $t('sessions.sessionCard.shouldUnlinkAppointmentBeforeDelete') }}
                    </q-tooltip>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="onCopySupportInfo"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="content_copy"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('common.copySupportInfo') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </q-item-section>
    </template>
    <div
      v-if="showAuditorCard"
      class="q-py-xs"
    >
      <audit-result-card
        :session-activity="sessionActivity"
        @refresh="onAuditResultUpdated"
      />
    </div>
    <div
      class="q-pb-md"
    >
      <session-activity-notes-editor
        v-if="!readOnly"
        :session-activity="sessionActivity"
        :session="session"
        :linked-appointment="linkedAppointment"
        :client="client"
        :all-programs="allPrograms"
        :apply-immediately="applyImmediately"
        :is-backup-different="isBackupDifferent"
        @changed="onChanged"
        @add-signature="onAddSignature"
        @delete-signature="onDeleteSignature"
        @request-signature="onRequestSignature"
        @cancel-signature-request="onCancelSignatureRequest"
        @show-backup-notes="$emit('show-backup-notes', $event)"
        @save="onSaveNotes"
        @scroll-to-banner="scrollToBannerEvent"
      />
    </div>
  </q-expansion-item>
</template>
<script>
import ViewAuditTrailDialog from 'components/Audit/ViewAuditTrailDialog';
import ChangeSessionActivityAppointment from 'components/SessionActivities/Modals/ChangeSessionActivityAppointment';
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import SessionActivityNotesEditor from 'components/SessionActivities/SessionActivityNotesEditor';
import SessionGoogleMapsPoints from 'components/Sessions/SessionGoogleMapsPoints';
import AppointmentChip from 'components/UI/AppointmentChip';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { arrayRemove, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
import { chain, forEach, get, isEmpty, isNull, isNumber, isObject, map, pick } from 'lodash';
import { mapState } from 'pinia';
import {
  HUMANIZED_INTEGRATION_NAMES,
  INTEGRATION_NAMES,
} from 'src/constants/integrations';
import { SESSION_ACTIVITY_STATUSES } from 'src/constants/sessionActivities';
import {
  AuditResultCard,
  PRE_SIGN_AUDIT_FIELD,
  getPreSignAuditId,
  isStoredAuditRunInFlight,
  useAuditRunClock,
  useAuditRunState,
  useAuditorAccess,
  wasAuditedBeforeSigning,
} from 'src/domains/noteAuditor';
import AppointmentsDB from 'src/fb/appointments-db';
import GenericDb from 'src/fb/generic-db';
import MessagesDB from 'src/fb/messages-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import Auth from 'src/mixins/Auth';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import Toolbox from 'src/mixins/Toolbox';
import { sessionAnalytics } from 'src/services/analytics';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import AppUtils, { copySupportInfoToClipboard } from 'src/utils/AppUtils';
import { BACKUP_NOTES } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'SessionActivityItem',

  components: {
    AuditResultCard,
    AppointmentChip,
    SessionActivityNotesEditor,
    UniversalTooltip,
  },

  // @todo Most of these mixins are probably not needed anymore
  mixins: [
    Auth,
    Integrations,
    Interactions,
    SessionActivitiesMixin,
    SessionExportMixin,
    Toolbox,
  ],

  async beforeRouteLeave(to, from, next) {
    await this.unbindBackupsData();
    await this.unbindBackupNotes();
    next();
  },

  props: {
    sessionActivity: {
      type: Object,
      required: true,
    },

    expand: {
      type: Boolean,
      default: false,
    },

    bold: {
      type: Boolean,
      default: false,
    },

    unfocused: {
      type: Boolean,
      default: false,
    },

    hideExpandIcon: {
      type: Boolean,
      default: false,
    },

    showSessionName: {
      type: Boolean,
      default: false,
    },

    applyImmediately: {
      type: Boolean,
      default: false,
    },

    linkedAppointment: {
      type: Object,
      default: () => ({}),
    },

    disableChangeLinkedAppointment: {
      type: Boolean,
      default: false,
    },

    session: {
      type: Object,
      default: null,
    },

    allPrograms: {
      type: Array,
      default: () => ([]),
    },

    client: {
      type: Object,
      default: null,
    },

    hasOtherNotesBackup: {
      type: Boolean,
      default: false,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    notesSending: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    // in ViewSession component redirect to History page
    'close',
    'input',
    'changed',
    'delete-request',
    'session-activity-updated',
    'session-activity-closed',
    'show-backup-notes',
    'appointments-updated',
    'session-updated',
    'timestamps-changed',
    'scroll-to-banner',
    'send-to-integration',
  ],

  setup() {
    const { canViewAuditorOutput } = useAuditorAccess();
    const { isAuditRunUnresolved } = useAuditRunState();

    return {
      canViewAuditorOutput,
      isAuditRunUnresolved,
      auditRunClock: useAuditRunClock(),
    };
  },

  data() {
    return {
      isSavingSessionNotes: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'integrationSettings',
      'hasTrainerCredentials', 'hasTherapistCredentials', 'hasSupervisorCredentials',
      'isManagerOrAdministrator', 'isSubscriptionPremium']),
    ...mapState(network, ['networkOnline', 'offlineMode']),

    canGoToIntegrationAppointment() {
      return this.linkedAppointment?.integrationExternalUrl
        && this.linkedAppointment?.integration === INTEGRATION_NAMES.LUMARY;
    },

    goToIntegrationAppointmentLabel() {
      return this.$t(
        'sessions.sessionCard.buttonGoToIntegrationAppointment',
        { integration: HUMANIZED_INTEGRATION_NAMES[this.connectedClinic.integration] },
      );
    },

    userCanViewAuditTrail() {
      return this.isManagerOrAdministrator || this.userDetails.admin;
    },

    isOffline() {
      return !this.networkOnline && !this.offlineMode;
    },

    disableAuditTrail() {
      return !this.isSubscriptionPremium || this.offlineMode;
    },

    // A run this client started shows before it lands, or the card appears out of nowhere later.
    showAuditorCard() {
      return this.canViewAuditorOutput
        && (!!this.sessionActivity.audit?.verdict
          || this.isAuditRunUnresolved(this.sessionActivity.id)
          || isStoredAuditRunInFlight(this.sessionActivity, this.auditRunClock));
    },

    hasLinkedAppointment() {
      return !isEmpty(this.linkedAppointment);
    },

    isBackupDifferent() {
      return this.hasOtherNotesBackup && this.canChangeSessionNotes
        && !this.isSavingSessionNotes && !this.isOffline && !this.offlineMode
        && !['closed', 'pending-signatures', 'rendered', 'integration-completed'].includes(this.sessionActivity.status);
    },

    isSessionTypeWithoutPrograms() {
      return ['form'].includes(this.session?.sessionType);
    },

    isSessionInProgress() {
      if (this.session === null) {
        return false;
      }

      return !['done', 'processing'].includes(this.session.status);
    },

    canFinishSessionActivity() {
      return this.hasTherapistCredentials
        && ['in-progress'].includes(this.sessionActivity.status)
        && (!this.isSessionInProgress);
    },

    showFinishAppointment() {
      return ['pending-signatures', 'closed', 'finished', 'rendered'].includes(this.sessionActivity.status)
        && this.isLinkedAppointmentInProgress
        && (!this.isSessionInProgress)
        && this.userCanFinishAppointment;
    },

    userCanFinishAppointment() {
      return this.hasSupervisorCredentials
        || (this.hasTherapistCredentials && this.linkedAppointment?.user === this.userDetails.userId);
    },

    isLinkedAppointmentInProgress() {
      return this.linkedAppointment?.status === 'in-progress';
    },

    hasGeolocation() {
      return isObject(this.sessionActivity.startGeolocation);
    },

    canEditSessionActivity() {
      return (
        this.hasTrainerCredentials
          || (
            this.sessionActivity.user === this.userDetails.userId
            && this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame')
          )
          || (
            this.sessionActivity.user !== this.userDetails.userId
            && this.userHasPrivilege('sessionActivities/editAssociateActivityTimeFrame')
          )
      ) && this.sessionActivity.status === 'finished';
    },

    canReopenSessionActivity() {
      return this.userHasPrivilege('sessionActivities/reopen')
        && ['pending-signatures', 'closed'].includes(this.sessionActivity.status)
        && this.canChangeSessionNotes && !this.isOffline;
    },

    isRenderedWithAllowImmediateSync() {
      const { status, renderedTimestamp } = this.sessionActivity;

      // notes get sent, so we receive a 'renderedTimestamp' but the status stays 'pending-signatures' as
      // allowImmediateSync is used to send notes without all the signatures completed
      return status === 'pending-signatures' && renderedTimestamp;
    },

    canUnrenderSessionActivity() {
      return this.userHasPrivilege('sessionActivities/unrender')
        && (
          ['rendered', 'integration-completed'].includes(this.sessionActivity.status)
          || this.isRenderedWithAllowImmediateSync
        );
    },

    canAddLinkedAppointment() {
      return !this.sessionActivity.appointment
        && (this.sessionActivity.status !== 'rendered')
        && this.hasTrainerCredentials;
    },

    canEditLinkedAppointment() {
      return (!this.readOnly)
        && !['rendered', 'integration-completed'].includes(this.sessionActivity.status)
        && this.hasTrainerCredentials;
    },

    canUnlinkAppointment() {
      return this.sessionActivity.appointment && this.canEditLinkedAppointment;
    },

    canDeleteSessionActivity() {
      return this.hasTrainerCredentials
        && this.userHasPrivilege('sessionActivities/delete')
        && ['finished', 'pending-signatures'].includes(this.sessionActivity.status)
        && !this.hasLinkedAppointment;
    },

    shouldUnlinkAppointmentFromActivity() {
      return !isNull(this.sessionActivity.appointment);
    }, // shouldUnlinkAppointmentFromActivity()

    isExportableSessionActivity() {
      return ['closed', 'rendered', 'integration-completed'].includes(this.sessionActivity.status);
    },

    hasDistance() {
      return !!this.activityTypesSettings[this.sessionActivity.type]?.hasDistance;
    },

    isGroupSession() {
      return get(this.linkedAppointment, 'isGroupSession', false);
    },

    formattedSessionActivityDistance() {
      if (this.hasDistance && isNumber(this.sessionActivity.distance)) {
        return `${this.formatDistance(this.sessionActivity.distance, this.connectedClinic.country)}, `;
      }
      return '';
    },

    formattedSessionActivityDate() {
      if (!(this.sessionActivity.startTimestamp instanceof Timestamp)) {
        return '';
      }

      const startTimeLabel = `${this.getLocaleDateString(this.sessionActivity.startTimestamp)
      } ${this.getLocaleTimeString(this.sessionActivity.startTimestamp, {
        options: { hour: '2-digit', minute: '2-digit' },
      })}`;

      if (!(this.sessionActivity.endTimestamp instanceof Timestamp)) {
        return `${startTimeLabel}`;
      }

      return `${startTimeLabel} - ${this.getLocaleTimeString(this.sessionActivity.endTimestamp, {
        options: { hour: '2-digit', minute: '2-digit' },
      })}`;
    },

    sessionActivityTitle() {
      return this.showSessionName
        ? this.sessionActivity.sessionName || this.getSessionActivityTypeLabel(this.sessionActivity)
        : this.sessionActivity.userName;
    },

    hasSession() {
      return this.session !== null;
    },

  },

  async created() {
    await Promise.all([
      this.bindBackupsData(this.sessionActivity.session),
      this.bindBackupNotes(this.sessionActivity.session),
    ]);
  },

  async beforeUnmount() {
    await Promise.all([
      this.unbindBackupsData(),
      this.unbindBackupNotes(),
    ]);
  },

  methods: {

    onSwitchSessionActivity(isOpen) {
      this.$emit('input', isOpen);
    },

    getValidatedIntegrationUrl(externalUrl) {
      if (typeof externalUrl !== 'string' || !externalUrl.trim()) {
        this.notifyNegative(this.$t('sessions.sessionCard.invalidIntegrationLinkMessage'));
        return null;
      }

      try {
        const parsedUrl = new URL(externalUrl);

        if (parsedUrl.protocol !== 'https:') {
          this.notifyNegative(this.$t('sessions.sessionCard.invalidIntegrationLinkMessage'));
          return null;
        }

        return parsedUrl;
      } catch (err) {
        this.notifyNegative(this.$t('sessions.sessionCard.invalidIntegrationLinkMessage'));
        if (err?.message.includes('Invalid URL')) {
          logEvent('Invalid URL to integration appointment', {
            context: {
              context_data: {
                integration: this.connectedClinic.integration,
                url: externalUrl,
                appointmentId: this.linkedAppointment?.id || null,
              },
            },
          });
        }
        return null;
      }
    },

    getIntegrationItemUrl() {
      if (!this.canGoToIntegrationAppointment) {
        return null;
      }

      return this.getValidatedIntegrationUrl(this.linkedAppointment?.integrationExternalUrl);
    },

    onGoToIntegrationAppointment() {
      window.open(this.getIntegrationItemUrl(), '_blank', 'noopener,noreferrer');
    },

    async onCopySessionNotesToClipboard(sessionActivity) {
      const exportData = this.generateSessionNotesHtml(sessionActivity.notes, sessionActivity.signatures);

      try {
        await this.copyHtmlToClipboard(exportData);
        this.notifySuccess(this.$t('sessions.sessionCard.notifyCopySessionNotesToClipboardComplete'));
      } catch (error) {
        this.notifyNegative(error.message);
      }
    }, // onCopySessionNotesToClipboard()

    /**
     * Export session notes to PDF
     *
     * @param {Object} sessionActivity
     * @param {boolean} includeData
     * @param {Object[] | []} allPrograms
     */
    async onExportSessionNotesToPdf(sessionActivity, includeData = false, allPrograms = []) {
      const { session, client, notes, signatures } = sessionActivity;

      try {
        this.$q.loading.show({ delay: 0 });

        const exportData = includeData ? await this.exportSessionDataToPdf(session, client, allPrograms) : [];

        const contentPages = [
          this.getSessionNotePdfPage(exportData, notes, signatures),
          ...await this.getAddendumPdfPages(sessionActivity),
        ];

        const layoutFieldsData = await this.getLayoutFieldsData({
          sessionActivity,
          session: this.session,
          client: this.client,
          appointment: !isEmpty(this.linkedAppointment) ? this.linkedAppointment : null,
        });
        await this.downloadSessionNotesPdf(contentPages, layoutFieldsData);
      } catch (err) {
        this.notifyNegative(err.message || this.$t('common.errors.export'));
        console.error(`Exporting error: ${err.message}`);
        logEvent(err, {
          context: {
            context_data: {
              cause: err.message,
              sessionActivity: sessionActivity.id,
            },
          },
        });
      } finally {
        this.$q.loading.hide();
      }
    }, // onExportSessionNotesToPdf()

    onLinkAppointment() {
      if (!this.canEditLinkedAppointment || this.disableChangeLinkedAppointment) {
        return;
      }
      const startDate = this.sessionActivity.startTimestamp instanceof Timestamp
        ? this.sessionActivity.startTimestamp.toDate()
        : new Date();
      const appointmentId = get(this.sessionActivity, 'appointment') || '';

      this.openComponentDialog(ChangeSessionActivityAppointment, {
        appointmentId,
        clientId: this.sessionActivity.client,
        userId: this.sessionActivity.user,
        startDate,
        allowUnlink: true,
      }, async (newAppointmentId) => {
        const appointmentsDb = new AppointmentsDB();
        const appointmentActions = [];
        if (appointmentId) {
          // Unlink previous appointment
          const appointment = await appointmentsDb.read(appointmentId);
          if (['completed', 'in-progress'].includes(appointment.status)) {
            // Set the old appointment back to scheduled - unless we already know it was canceled, deleted, etc.
            // If the appointment was rendered, and we somehow unlinked it through this method, we need to unrender it
            // @todo: updating `session: null` breaks the auto-mapping feature
            appointmentActions.push(
              appointmentsDb.set({
                sessionActivity: null,
                session: null,
                status: 'scheduled',
                ...((appointment.isRendered || this.isRenderedWithAllowImmediateSync) && {
                  isRendered: false,
                  renderedTimestamp: null,
                  renderedByUser: null,
                }),
              }, appointmentId),
            );
          }
        }
        let appointmentData = {};
        if (newAppointmentId !== null) {
          // @todo: why not just return the whole appointment object from ChangeSessionActivityAppointment
          const newAppointment = await appointmentsDb.read(newAppointmentId);
          appointmentData = pick(
            newAppointment,
            [
              'integrationReference',
              'integrationClientParRef',
              'integrationStaffParRef',
              'integrationClientRef',
              'integrationStaffRef',
              'integrationAppointmentType',
              'integration',
              'integrationExternalUrl',
            ],
          );
          appointmentActions.push(
            appointmentsDb.set({
              sessionActivity: this.sessionActivity.id,
              status: this.sessionActivity.status === 'in-progress' ? 'in-progress' : 'completed',
              session: this.session?.id ?? null,
            }, newAppointmentId),
          );
        }
        Promise.all(appointmentActions);
        await this.saveSessionActivityChanges({
          appointment: newAppointmentId,
          appointmentData,
          appointmentSource: null,
          ...(!newAppointmentId && this.sessionActivity.status === 'rendered' && {
            status: 'closed',
            renderedTimestamp: null,
          }),
        });

        if (newAppointmentId) {
          sessionAnalytics.trackActivityLinkedToAppointmentManually({
            sessionActivityId: this.sessionActivity.id,
            sessionActivityType: this.sessionActivity.type,
            appointmentId: newAppointmentId,
          });
        }
      });
    },

    onUnlinkAppointment() {
      const confirmMessage = this.connectedClinic.integration
        ? this.$t('run.overview.confirmUnlinkAppointment', {
          integrationLabel: this.getIntegrationName(this.connectedClinic.integration),
        })
        : this.$t('run.overview.confirmUnlinkAppointmentNoIntegration');

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        confirmMessage,
        async () => {
          const appointmentId = get(this.sessionActivity, 'appointment');
          if (!appointmentId) {
            return;
          }

          let appointmentChangeAction;

          try {
            const appointmentsDb = new AppointmentsDB();
            const appointment = await appointmentsDb.read(appointmentId);

            // @todo: updating `session: null` breaks the auto-mapping feature
            const appointmentChanges = {
              sessionActivity: null,
              session: null,
              ...(
                (appointment.isRendered || this.isRenderedWithAllowImmediateSync) && {
                  isRendered: false,
                  renderedTimestamp: null,
                  renderedByUser: null,
                }),
            };

            if (['in-progress', 'completed'].includes(appointment.status)) {
              // Only set appointment status back to scheduled if it wasn't canceled, deleted, etc.
              appointmentChanges.status = 'scheduled';
            }

            appointmentChangeAction = appointmentsDb.set(appointmentChanges, appointmentId);
          } catch (err) {
            logEvent(err);
          }

          const unlinkAppointmentsResults = await Promise.allSettled([
            appointmentChangeAction,
            this.saveSessionActivityChanges({
              appointment: null,
              appointmentData: {},
              appointmentSource: null,
              ...((this.sessionActivity.status === 'rendered' || this.isRenderedWithAllowImmediateSync) && {
                status: this.isRenderedWithAllowImmediateSync ? 'pending-signatures' : 'closed',
                renderedTimestamp: null,
              }),
            }),
          ]);

          forEach(unlinkAppointmentsResults, (unlinkResult) => {
            if (unlinkResult.status === 'rejected') {
              logEvent(unlinkResult.reason);
              this.notifyNegative(unlinkResult.reason);
            }
          });
        },
      );
    }, // onUnlinkAppointment()

    async onFinishSessionActivity() {
      const status = await this.applySessionActivityFinishedStatus(
        this.sessionActivity.id,
        'SessionActivityItem.onFinishSessionActivity',
      );

      const newSessionActivity = {
        ...this.sessionActivity,
        status,
        endTimestamp: this.sessionActivity.endTimestamp || Timestamp.now(),
      };

      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity: newSessionActivity,
        title: this.$t('sessions.modals.sessionActivity.dialogTitleFinish'),
      }, async (sessionActivityChanges) => {
        if (this.sessionActivity.type === 'supervision') {
          await this.endSupervision(this.sessionActivity.session);
        }
        // Handle save
        this.saveSessionActivityChanges({
          ...sessionActivityChanges,
          status,
        }, true);

        // End appointment session activity
        if (this.isLinkedAppointmentInProgress) {
          await this.finishAppointment();
          await this.refreshLumaryAppointments(this.connectedClinic.integration, this.linkedAppointment);
        }
      });
    },

    async endSupervision(sessionId) {
      const sessionsDb = new SessionsDB();
      const sessionChanges = {
        supervisedBy: null,
        supervisedByName: null,
      };
      await sessionsDb.set(sessionChanges, sessionId);
      this.$emit('session-updated', sessionChanges);
    },

    async finishAppointment(notify = false) {
      const appointmentsDb = new AppointmentsDB();
      await appointmentsDb.set({
        status: 'completed',
      }, this.linkedAppointment.id);
      this.$emit('appointments-updated');

      if (notify) {
        this.notifySuccess(this.$t('common.notifyChangesSaved'));
      }
    },

    onShowGeolocation() {
      this.onOpenMap(this.sessionActivity.startGeolocation, 'start');
    },

    onEditSessionActivity() {
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity: this.sessionActivity,
      }, async (sessionActivityChanges) => {
        // Handle save
        this.saveSessionActivityChanges(sessionActivityChanges, true);
      });
    }, // onEditSessionActivity()

    async onReopenSessionActivity() {
      if (isEmpty(this.sessionActivity.requiredSignatories)
        && isEmpty(this.sessionActivity.signatures)) {
        await this.reopenSessionActivity();
        this.notifySuccess(this.$t('sessions.sessionCard.notifyReopenSessionActivity'));
        return;
      }

      if (this.userHasPrivilege('sessionActivities/preserveSignatures')) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmReopenActivityWithSignatures'),
          async () => {
            await this.reopenSessionActivity(true);
            this.notifySuccess(this.$t('sessions.sessionCard.notifyReopenSessionActivity'));
          },
        );
      } else {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmReopenActivity'),
          async () => {
            await this.reopenSessionActivity();
            this.notifySuccess(this.$t('sessions.sessionCard.notifyReopenSessionActivity'));
          },
        );
      }
    }, // onReopenSessionActivity()

    async onUnrenderSessionActivity() {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('sessions.sessionCard.confirmUnrenderSessionActivity'),
        async () => {
          await this.unrenderSessionActivity();
          this.notifySuccess(this.$t('sessions.sessionCard.notifyUnrenderSessionActivity'));
        },
      );
    }, // onUnrenderSessionActivity()

    onDeleteSessionActivity() {
      if (this.shouldUnlinkAppointmentFromActivity) {
        return;
      }

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('sessions.sessionCard.confirmDeleteSessionActivity'),
        async () => {
          this.$emit('delete-request', this.sessionActivity);
        },
      );
    }, // onDeleteSessionActivity()

    onCopySupportInfo() {
      copySupportInfoToClipboard({ session: this.session?.id, 'session activity': this.sessionActivity?.id });
    }, // onCopySupportInfo()

    onAuditResultUpdated() {
      this.$emit('session-activity-updated');
    },

    onChanged(isChanged) {
      this.$emit('changed', isChanged);
    },

    onAddSignature(signature) {
      this.addSignature(signature);
    },

    onDeleteSignature(signature) {
      this.deleteSignature(signature);
    },

    onRequestSignature(requiredSignatory) {
      this.requestSignature(requiredSignatory);
    },

    onCancelSignatureRequest(requiredSignatory) {
      this.cancelSignatureRequest(requiredSignatory);
    },

    async onSaveNotes(changes, saveToBackupOnly = false, sendToIntegration = false, overrideAppointmentData = null) {
      try {
        this.isSavingSessionNotes = true;

        if (saveToBackupOnly) {
          return this.backupSessionActivityNotes(changes);
        }

        const results = await AppUtils.allSettled([
          this.backupSessionActivityNotes(changes),
          this.saveSessionActivityChanges(changes),
        ]);

        const hasFailure = results?.some(({ status }) => status === 'rejected');

        if (sendToIntegration && !hasFailure) {
          this.$emit('send-to-integration', this.sessionActivity.id, overrideAppointmentData);
        }
      } finally {
        this.isSavingSessionNotes = false;
      }
    },

    async backupSessionActivityNotes(changes) {
      if (!this.hasSession) {
        return;
      }

      const activityNotes = get(changes, 'notes', '');
      const currentSessionNotesFieldData = get(changes, 'sessionNotesFieldData', null);

      const aggregatedBackupData = {
        id: `backupNotes_${this.sessionActivity.id}`,
        session: this.sessionActivity.session,
        client: this.sessionActivity.client,
        notes: activityNotes,
        fieldsData: currentSessionNotesFieldData,
        isBackupAddedOnline: (this.networkOnline || this.offlineMode),
      };

      const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);
      await sessionBackupNotesDb.set(aggregatedBackupData, aggregatedBackupData.id);
    }, // backupSessionActivityNotes()

    onViewAuditTrail() {
      this.openComponentDialog(ViewAuditTrailDialog, {
        collection: 'sessionActivities',
        resourceId: this.sessionActivity.id,
      });
    },

    async onExportSessionNotesAndDataToPdf() {
      this.onExportSessionNotesToPdf(this.sessionActivity, true, this.allPrograms);
    }, // onExportSessionNotesAndDataToPdf()

    onOpenMap(location, mapHeaderLabel) {
      const coords = { lat: location.latitude, lng: location.longitude };
      let title = get(location, 'place', null);
      if (title === null) {
        title = (mapHeaderLabel === 'start')
          ? this.$t('sessions.modals.geolocation.startSessionLabel')
          : this.$t('sessions.modals.geolocation.endSessionLabel');
      }
      this.openComponentDialog(SessionGoogleMapsPoints, {
        coords,
        title,
      });
    }, // onOpenMap()

    async updateSessionActivity(sessionActivityChanges) {
      const wasReopened = this.sessionActivity.wasReopened === true;
      const isNotesUpdated = ('notes' in sessionActivityChanges)
        && this.sessionActivity.notes !== sessionActivityChanges.notes;

      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.update(sessionActivityChanges);

      if (wasReopened && isNotesUpdated) {
        sessionAnalytics.trackActivityNotesEditedAfterReopened({
          sessionActivityId: this.sessionActivity.id,
          sessionActivityType: this.sessionActivity.type || null,
        });
      }

      if ([SESSION_ACTIVITY_STATUSES.CLOSED].includes(sessionActivityChanges.status)) {
        sessionAnalytics.trackActivityCompleted({
          sessionActivityId: this.sessionActivity.id,
          sessionActivityType: this.sessionActivity.type || null,
        });
      }
    }, // updateSessionActivity()

    async unrenderSessionActivity() {
      this.$q.loading.show();

      const genericDb = new GenericDb();
      try {
        await genericDb.executeTransaction(async (transactionMethods) => {
          const appointmentsDb = new AppointmentsDB();
          const sessionActivitiesDb = new SessionActivitiesDB();

          // @todo (HR-1424) Read integrationStatus and check if we are allowed to unrender the appointment
          // if not, then alert the user and do not unrender the appointment

          let isProviderMismatch = false;
          if (this.sessionActivity.appointment) {
            const appointment = await transactionMethods.read(appointmentsDb, {
              id: this.sessionActivity.appointment,
            });
            isProviderMismatch = !!appointment && (
              appointment.user !== this.sessionActivity.user
              || appointment.client !== this.sessionActivity.client
            );
          }

          const writePromises = [];

          if (this.sessionActivity.appointment) {
            writePromises.push(
              transactionMethods.set(appointmentsDb, {
                id: this.sessionActivity.appointment,
                data: isProviderMismatch
                  ? {
                    sessionActivity: null,
                    isRendered: false,
                    renderedTimestamp: null,
                    renderedByUser: null,
                    status: 'scheduled',
                    actualStartTimestamp: null,
                    actualEndTimestamp: null,
                    actualDuration: null,
                  }
                  : {
                    isRendered: false,
                    renderedTimestamp: null,
                    renderedByUser: null,
                    isUnrendered: true,
                    status: 'completed',
                  },
              }),
            );
          }

          writePromises.push(
            transactionMethods.set(sessionActivitiesDb, {
              id: this.sessionActivity.id,
              data: {
                status: this.isRenderedWithAllowImmediateSync ? 'pending-signatures' : 'closed',
                renderedTimestamp: null,
                ...(isProviderMismatch && {
                  appointment: null,
                  appointmentData: null,
                  appointmentSource: null,
                }),
              },
            }),
          );

          await Promise.all(writePromises);
        });
        this.$emit('session-activity-updated');
        this.$emit('appointments-updated');
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.$q.loading.hide();
      }
    }, // unrenderSessionActivity()

    async reopenSessionActivity(preserveSignatures = false) {
      const cancelRequestActions = (this.sessionActivity.requiredSignatories || [])
        .map((requiredSignatory) => this.cancelSignatureRequestComments(
          requiredSignatory,
          this.session?.id,
          this.sessionActivity.client,
        ));

      const sessionActivitiesDb = new SessionActivitiesDB();
      const { id, sessionNotesFieldData } = this.sessionActivity;
      let additionalRewriteProperties = {};
      // resetting start and finish session time fields on activity reopen
      // used for notes editor to show activity time instead of previously saved fields time
      if (sessionNotesFieldData?.session) {
        additionalRewriteProperties = {
          sessionNotesFieldData: {
            ...sessionNotesFieldData,
            session: {
              ...sessionNotesFieldData.session,
              sessionStartTime: null,
              sessionFinishTime: null,
            },
          },
        };
      }
      /*
       * Read before the update below clears it: the marker describes the signing cycle being undone,
       * and the binding would already have dropped it by the time the event is sent.
       */
      const reopenedAuditContext = {
        wasAuditedBeforeSigning: wasAuditedBeforeSigning(this.sessionActivity),
        sessionActivityAuditId: getPreSignAuditId(this.sessionActivity),
      };

      const activityUpdateAction = sessionActivitiesDb.update({
        ...(!preserveSignatures ? { signatures: [] } : {}),
        // Spent with the signature it belonged to: the next one is unreviewed until the auditor runs.
        [PRE_SIGN_AUDIT_FIELD]: null,
        requiredSignatories: [],
        requiredSignatoriesUserIds: [],
        status: await this.applySessionActivityFinishedStatus(
          id,
          'SessionActivityItem.reopenSessionActivity',
          true,
        ),
        id,
        wasReopened: true,
        reopenedTimestamp: serverTimestamp(),
        ...additionalRewriteProperties,
      }).then(() => {
        sessionAnalytics.trackActivityReopened({
          sessionActivityId: this.sessionActivity.id,
          sessionActivityType: this.sessionActivity.type,
          ...reopenedAuditContext,
        });
      });

      await Promise.all([
        activityUpdateAction,
        ...cancelRequestActions,
      ]);

      this.$emit('session-activity-updated');
    }, // reopenSessionActivity()

    async addSignature(signature) {
      let resolveRequest;
      if (signature.userId === null && signature.label) {
        resolveRequest = chain(this.sessionActivity)
          .get('requiredSignatories', [])
          .find({ label: signature.label })
          .value();
      } else {
        resolveRequest = this.sessionActivity.requiredSignatories?.find(
          ({ userId, clientDataField }) => (
            userId === signature.userId && clientDataField === signature.clientDataField
          ),
        );
      }

      const newSignature = {
        ...signature,
        ...pick(resolveRequest, ['clientDataField', 'label', 'clientDataField']),
      };

      const sessionActivityChanges = {
        signatures: arrayUnion(newSignature),
        id: this.sessionActivity.id,
      };

      if (resolveRequest) {
        let isCompleted = false;
        sessionActivityChanges.requiredSignatories = arrayRemove(resolveRequest);

        const existingRequiredSignatoriesUserIds = this.sessionActivity.requiredSignatoriesUserIds || [];

        sessionActivityChanges.requiredSignatoriesUserIds = existingRequiredSignatoriesUserIds.filter(
          (userId) => userId !== resolveRequest?.userId,
        );

        // Check if we should close the session activity
        const newStatus = this.getNewSessionActivityStatus(this.sessionActivity, sessionActivityChanges);

        if (newStatus !== this.sessionActivity.status) {
          sessionActivityChanges.status = newStatus;

          if (['closed', 'rendered'].includes(newStatus)) {
            isCompleted = true;
          }
        }

        await Promise.all([
          this.updateSessionActivity(sessionActivityChanges),
          this.resolveSignatureRequestComments(resolveRequest),
        ]);

        this.$emit('session-activity-updated');
        if (isCompleted) {
          await this.afterSessionActivityComplete({ ...this.sessionActivity, ...sessionActivityChanges });
        }
        return;
      }

      await this.updateSessionActivity(sessionActivityChanges);
      this.$emit('session-activity-updated');
    },

    async deleteSignature(signature) {
      const sessionActivitiesDb = new SessionActivitiesDB();
      const sessionActivityChanges = {
        signatures: arrayRemove(signature),
        id: this.sessionActivity.id,
      };
      await sessionActivitiesDb.update(sessionActivityChanges);

      if (this.offlineMode && signature.id) {
        const filesUploadingDb = new GenericIndexedDb('filesUploading');
        await filesUploadingDb.delete(signature.id, true);
      }

      this.$emit('session-activity-updated');
    },

    async requestSignature(requiredSignatory) {
      let isCompleted = false;

      const sessionActivitiesDb = new SessionActivitiesDB();

      const existingRequiredSignatoriesUserIds = this.sessionActivity.requiredSignatoriesUserIds || [];

      const sessionActivityChanges = {
        requiredSignatories: arrayUnion(requiredSignatory),
        requiredSignatoriesUserIds: [...existingRequiredSignatoriesUserIds, requiredSignatory.userId].filter(Boolean),
        id: this.sessionActivity.id,
      };
      const newStatus = this.getNewSessionActivityStatus(this.sessionActivity, sessionActivityChanges);

      if (newStatus !== this.sessionActivity.status) {
        sessionActivityChanges.status = newStatus;
        if (['closed', 'rendered'].includes(newStatus)) {
          isCompleted = true;
        }
      }

      await sessionActivitiesDb.update(sessionActivityChanges);
      this.$emit('session-activity-updated');
      if (isCompleted) {
        await this.afterSessionActivityComplete({ ...this.sessionActivity, ...sessionActivityChanges });
      }
    },

    async cancelSignatureRequest(requiredSignatory) {
      let isCompleted = false;

      const existingRequiredSignatoriesUserIds = this.sessionActivity.requiredSignatoriesUserIds || [];

      const sessionActivityChanges = {
        id: this.sessionActivity.id,
        requiredSignatories: arrayRemove(requiredSignatory),
        requiredSignatoriesUserIds: existingRequiredSignatoriesUserIds.filter(
          (userId) => userId !== requiredSignatory?.userId,
        ),
      };
      const newStatus = this.getNewSessionActivityStatus(this.sessionActivity, sessionActivityChanges);

      if (newStatus !== this.sessionActivity.status) {
        sessionActivityChanges.status = newStatus;
        if (['closed', 'rendered'].includes(newStatus)) {
          isCompleted = true;
        }
      }

      await Promise.all([
        this.updateSessionActivity(sessionActivityChanges),
        this.cancelSignatureRequestComments(requiredSignatory, this.session?.id, this.sessionActivity.client),
      ]);
      this.$emit('session-activity-updated');
      if (isCompleted) {
        await this.afterSessionActivityComplete({ ...this.sessionActivity, ...sessionActivityChanges });
      }
    },

    async resolveSignatureRequestComments(signatory) {
      if (this.session === null) {
        return;
      }

      const messagesDb = new MessagesDB();
      const comments = await messagesDb.readAll([
        ['clinic', '==', this.userDetails.clinic],
        ['client', '==', this.session.client],
        ['session', '==', this.session.id],
        ['type', '==', 'signatureRequest'],
        ['signatoryId', '==', signatory.uid]]);
      return map(comments, (comment) => (
        messagesDb.update({ resolved: true, id: comment.id })
      ));
    },

    async saveSessionActivityChanges(changes, notify = false) {
      const status = this.getNewSessionActivityStatus(this.sessionActivity, changes);

      const prevStartTimestamp = this.sessionActivity.startTimestamp instanceof Timestamp
        ? this.sessionActivity.startTimestamp.toMillis()
        : null;
      const newStartTimestamp = changes.startTimestamp instanceof Timestamp
        ? changes.startTimestamp.toMillis()
        : null;
      const prevEndTimestamp = this.sessionActivity.endTimestamp instanceof Timestamp
        ? this.sessionActivity.endTimestamp.toMillis()
        : null;
      const newEndTimestamp = changes.endTimestamp instanceof Timestamp
        ? changes.endTimestamp.toMillis()
        : null;
      const hasTimestampsChanged = prevStartTimestamp !== newStartTimestamp || prevEndTimestamp !== newEndTimestamp;

      await this.updateSessionActivity({
        ...changes,
        status,
        id: this.sessionActivity.id,
      });
      this.$emit('session-activity-updated');
      if (['closed', 'rendered'].includes(status)) {
        await this.afterSessionActivityComplete({ ...this.sessionActivity, ...changes, status });
      }
      if (hasTimestampsChanged) {
        // Make sure that parent has a change to recalculate session duration if needed
        this.$emit('timestamps-changed');
      }

      if (notify) {
        this.notifySuccess(this.$t('common.notifyChangesSaved'));
      }
    }, // saveSessionActivityChanges()

    async afterSessionActivityComplete(sessionActivity) {
      // @todo: consider handling activities with the type 'form' separately to avoid complicating the code,
      // and also add transactions there to improve the system’s reliability
      if (['closed', 'rendered'].includes(sessionActivity.status) && sessionActivity.clientFile) {
        this.$q.loading.show({ delay: 0 });
        try {
          const actions = [];
          const sessionActivitiesDb = new SessionActivitiesDB();

          // Activity is completed and should be saved to file
          const latestSessionActivity = await sessionActivitiesDb.read(this.sessionActivity.id);
          actions.push(this.saveFormToFile(latestSessionActivity, this.session));

          // Activity is completed, and its session should be marked as done
          if (sessionActivity.session) {
            const sessionsDb = new SessionsDB();

            const sessionChanges = {
              status: 'done',
              finishTimestamp: serverTimestamp(),
            };
            const updateAndEmit = async () => {
              await sessionsDb.set(sessionChanges, sessionActivity.session);
              this.$emit('session-updated', sessionChanges);
            };
            actions.push(updateAndEmit());
          }

          await AppUtils.allSettled(actions);
        } finally {
          this.$q.loading.hide();
        }
      }

      this.$emit('session-activity-closed');
    }, // afterSessionActivityComplete()

    scrollToBannerEvent(bannerElement) {
      this.$emit('scroll-to-banner', bannerElement);
    }, // scrollToBannerEvent

  },
};
</script>

<style lang="scss" scoped>

.activity-item {
  flex-direction: row;
  justify-content: start;
}

.unfocused-activity {
  opacity: 0.6;
}

</style>
